import { useCallback } from 'react';
import { useSessionState } from './session-state-hook';

const formReducer = (state, action) => {
  switch (action.type) {
    case 'INPUT_CHANGE':
      if (
        state.inputs[action.inputId]?.value === action.value &&
        state.inputs[action.inputId]?.isValid === action.isValid
      )
        return state;
      let formIsValid = true;
      for (const inputId in state.inputs) {
        if (!state.inputs[inputId]) {
          continue;
        }
        if (inputId === action.inputId) {
          formIsValid = formIsValid && action.isValid;
        } else {
          formIsValid = formIsValid && state.inputs[inputId].isValid;
        }
      }
      return {
        ...state,
        inputs: {
          ...state.inputs,
          [action.inputId]: { value: action.value, isValid: action.isValid }
        },
        isValid: formIsValid
      };
    case 'SET_DATA':
      return {
        inputs: action.inputs,
        isValid: action.formIsValid
      };
    default:
      return state;
  }
};

const serializableForm = (state) => {
  const inputs = Object.fromEntries(
    Object.entries(state.inputs).map(([id, input]) => [
      id,
      /password/i.test(id) || (input?.value && typeof input.value === 'object')
        ? { value: '', isValid: false }
        : input
    ])
  );
  return { inputs, isValid: Object.values(inputs).every((input) => input?.isValid) };
};

export const useForm = (initialInputs, initialFormValidity, storageKey = null) => {
  const [formState, setFormState, clearDraft, isStored] = useSessionState(
    storageKey,
    {
      inputs: initialInputs,
      isValid: initialFormValidity
    },
    {
      serialize: serializableForm,
      restore: (saved, fallback) => {
        const inputs = Object.fromEntries(
          Object.entries(fallback.inputs).map(([id, input]) => {
            const stored = saved?.inputs?.[id];
            const canRestore =
              !/password/i.test(id) &&
              id !== 'image' &&
              stored &&
              ['string', 'number'].includes(typeof stored.value);
            return [
              id,
              canRestore ? { value: stored.value, isValid: stored.isValid === true } : input
            ];
          })
        );
        return { inputs, isValid: Object.values(inputs).every((input) => input?.isValid) };
      }
    }
  );

  const inputHandler = useCallback(
    (id, value, isValid) => {
      setFormState((state) =>
        formReducer(state, {
          type: 'INPUT_CHANGE',
          value,
          isValid,
          inputId: id
        })
      );
    },
    [setFormState]
  );

  const setFormData = useCallback(
    (inputData, formValidity) => {
      setFormState((state) =>
        formReducer(state, {
          type: 'SET_DATA',
          inputs: inputData,
          formIsValid: formValidity
        })
      );
    },
    [setFormState]
  );

  return [formState, inputHandler, setFormData, clearDraft, isStored];
};
